import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex w-full flex-col gap-2 pb-2 md:flex-row md:flex-wrap" }

import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import { getTranslation, getGlobalTranslation } from "~/js/composables/translations";
import {
	mergeCategoryFacet,
	mergeOrderedTypeFacet,
	SEARCH_RESULTS_CANONICAL_TYPE_SLUGS,
} from "~/js/composables/hub-facet-merge";

import { Translations } from "~/js/interfaces/translations";
import { Category } from "~/js/interfaces/knowledge-hub";
import { Facet, FacetCount } from "~/js/interfaces/facets";

import { useSearchStore } from "~/js/store/search";

import FilterHubFacetDesktop from "~/vue/molecules/filters/FilterHubFacetDesktop.vue";
import FilterHubFacetMobileDrawer from "~/vue/molecules/filters/FilterHubFacetMobileDrawer.vue";

interface Props {
	translations?: Translations | null;
	industries: Array<Category>;
	solutions: Array<Category>;
	/** When set (non-empty), overrides which type facet slugs are always listed first. */
	canonicalTypeSlugs?: Array<string> | null;
}

const TYPE_FILTER_ALL = "__typeAll__";

const searchPanelClass =
	"absolute w-[calc(100vw_-_5rem)] lg:right-0 z-[120] rounded-3xl bg-white p-6 shadow-2xl lg:w-[250px]";

export default /*@__PURE__*/_defineComponent({
  __name: 'FilterSearchResults',
  props: {
    translations: { default: () => ({}) },
    industries: { default: () => [] },
    solutions: { default: () => [] },
    canonicalTypeSlugs: { default: null }
  },
  setup(__props: any) {

const props = __props;
const searchStore = useSearchStore();
const { loading, facets, filters } = storeToRefs(searchStore);
const mobileExpanded = ref(false);

const toggleMobile = () => {
	mobileExpanded.value = !mobileExpanded.value;
};

/** Sentinel for "All results" — cleared in `updateFacet`, never stored on `filters.type`. */
const orderedTypeCounts = computed((): FacetCount[] => {
	const typeLabels: Record<string, string> = {
		blog: getGlobalTranslation("Blog"),
		caseStudy: getGlobalTranslation("Case Study"),
		dossier: getGlobalTranslation("Dossier"),
		dossiers: getGlobalTranslation("Dossier"),
		file: getGlobalTranslation("File"),
		news: getGlobalTranslation("News"),
		pressRelease: getGlobalTranslation("Press Release"),
		whitepaper: getGlobalTranslation("Whitepaper"),
		event: getTranslation(props.translations, "Events & Webinars"),
	};

	const facet = facets.value?.find((f: Facet) => f.field_name === "type");
	const canonicalSlugs =
		props.canonicalTypeSlugs && props.canonicalTypeSlugs.length > 0
			? props.canonicalTypeSlugs
			: [...SEARCH_RESULTS_CANONICAL_TYPE_SLUGS];
	return mergeOrderedTypeFacet(facet, typeLabels, (raw) => getGlobalTranslation(raw), canonicalSlugs);
});

const industryFilter = computed((): Facet | null => {
	const facet = facets.value?.find((f: Facet) => f.field_name === "industries");
	return mergeCategoryFacet("industries", props.industries, facet);
});

const solutionsFilter = computed((): Facet | null => {
	const facet = facets.value?.find((f: Facet) => f.field_name === "solutions");
	return mergeCategoryFacet("solutions", props.solutions, facet);
});

const updateFacet = (facet: { field: string; value: string | boolean }) => {
	if (facet.field === "type" && facet.value === TYPE_FILTER_ALL) {
		searchStore.clearTypeFilter();
		return;
	}
	searchStore.setFilter(facet.field as keyof typeof filters.value, facet.value as string);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(FilterHubFacetDesktop, {
      loading: _unref(loading),
      filters: _unref(filters),
      "ordered-type-counts": orderedTypeCounts.value,
      translations: __props.translations,
      "type-filter-all-value": TYPE_FILTER_ALL,
      onSetFacet: _cache[0] || (_cache[0] = ($event: any) => (updateFacet($event)))
    }, null, 8, ["loading", "filters", "ordered-type-counts", "translations"]),
    _createVNode(FilterHubFacetMobileDrawer, {
      open: mobileExpanded.value,
      loading: _unref(loading),
      filters: _unref(filters),
      "ordered-type-counts": orderedTypeCounts.value,
      "industry-filter": industryFilter.value,
      "solutions-filter": solutionsFilter.value,
      translations: __props.translations,
      "type-filter-all-value": TYPE_FILTER_ALL,
      "ga-close-category": "filter-search-results-mobile-toggle",
      "container-id": "filter-mobile",
      "panel-class": searchPanelClass,
      "industry-multiselect-utilities": "w-full",
      "solutions-multiselect-utilities": "w-full",
      onToggle: toggleMobile,
      onSetFacet: _cache[1] || (_cache[1] = ($event: any) => (updateFacet($event)))
    }, null, 8, ["open", "loading", "filters", "ordered-type-counts", "industry-filter", "solutions-filter", "translations"])
  ]))
}
}

})